import { formatNaiveDate, formatNaiveDateTimeInput, parseNaiveDateInput, parseNaiveDateTimeInput } from '../../lib/naiveDateTime';

/**
 * Start/End inputs come in two shapes, driven by `allDay`:
 *
 * - `allDay` (the default, and what every holiday created from the client admin is): a whole
 *   calendar day, stored at midnight. The client admin has no time picker for these, the calendar
 *   matches them with `isBetween(..., 'day', '[]')`, and they are rendered as 'DD/MM/YYYY'.
 * - not `allDay`: the client admin labels this "Event" (HolidayGroupTabHolidays.tsx renders the
 *   flag as Full Day / Event), which is the case where a time of day is meaningful, so the inputs
 *   keep hours and minutes.
 *
 * In both shapes `format`/`parse` keep the value a naive string, so the browser timezone can never
 * leak into the payload — see src/lib/naiveDateTime.ts. [PROD-28995]
 */
export const wholeDayInputProps = {
  format: formatNaiveDate,
  parse: parseNaiveDateInput,
  helperText: "Calendar day in the holiday's country. A whole-day holiday stores no time of day."
};

export const timedInputProps = {
  format: formatNaiveDateTimeInput,
  parse: parseNaiveDateTimeInput,
  helperText: "Local time in the holiday's country — stored as entered, with no timezone conversion."
};

/**
 * Normalise before saving, so the stored value always matches the `allDay` flag even if the toggle
 * was flipped after the dates were picked.
 */
export const transformHolidayTemplate = (data: Record<string, unknown>) => {
  const toValue = data.allDay ? parseNaiveDateInput : parseNaiveDateTimeInput;
  return { ...data, startTime: toValue(data.startTime), endTime: toValue(data.endTime) };
};

const REQUIRED_TEXT_SOURCES = ['country', 'name', 'type', 'level'] as const;

// ISO 3166-1 alpha-2; the API rejects anything that is not exactly two characters.
const COUNTRY_CODE = /^[A-Za-z]{2}$/;

const isBlank = (value: unknown) => value == null || String(value).trim() === '';

/**
 * Form-level validation for the holiday template Create/Edit forms.
 *
 * It owns *all* of the form's validation on purpose: react-admin installs this as a react-hook-form
 * `resolver`, and react-hook-form skips per-field rules once a resolver is present. It also replaces
 * the `required` HTML attribute the inputs used to carry — a native bubble fires before the form
 * validates and blocks submit without an inline error, which is the false error in PROD-28995.
 */
export const validateHolidayTemplate = (values: Record<string, unknown>) => {
  const errors: Record<string, string> = {};

  REQUIRED_TEXT_SOURCES.forEach((source) => {
    if (isBlank(values?.[source])) {
      errors[source] = 'Required';
    }
  });

  if (!errors.country && !COUNTRY_CODE.test(String(values.country).trim())) {
    errors.country = 'Must be a 2-letter country code';
  }

  // Compare at the precision the form is actually collecting.
  const read = values?.allDay ? formatNaiveDate : formatNaiveDateTimeInput;
  const startTime = read(values?.startTime);
  const endTime = read(values?.endTime);

  // A value that is present but unreadable (a corrupt API value) is not the same as a missing one.
  const dateError = (raw: unknown) => (isBlank(raw) ? 'Required' : 'Invalid date');

  if (!startTime) errors.startTime = dateError(values?.startTime);
  if (!endTime) {
    errors.endTime = dateError(values?.endTime);
  } else if (startTime && endTime < startTime) {
    // Both are fixed-width 'YYYY-MM-DD[THH:mm]', so a string compare is a chronological compare.
    errors.endTime = values?.allDay ? 'End Date must be the same as or after Start Date' : 'End must be the same as or after Start';
  }

  return errors;
};
